import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { teacherAPI } from '../../../services/api';
import api from '../../../services/api';
import { toast } from 'react-hot-toast';

const StaffAssignQuiz = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter states
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [filters, setFilters] = useState({
    class_id: '',
    academic_session_id: '',
    term_id: '',
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch students when class, session, or term changes
  useEffect(() => {
    if (filters.class_id && filters.academic_session_id && filters.term_id) {
      fetchStudents();
    } else {
      setStudents([]);
      setFilteredStudents([]);
    }
  }, [filters.class_id, filters.academic_session_id, filters.term_id]);

  useEffect(() => {
    applyFilters();
  }, [students, searchTerm]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [classesRes, sessionsRes] = await Promise.all([
        api.get('/subject-teacher/my-classes'),
        api.get('/subject-teacher/academic-sessions'),
      ]);

      setClasses(classesRes.data || []);
      setSessions(sessionsRes.data || []);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load class or session data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/subject-teacher/students/${filters.class_id}`, {
        params: {
          academic_session_id: filters.academic_session_id,
          term_id: filters.term_id,
        },
      });
      const studentsData = response.data.students || [];
      setStudents(studentsData);
      setSelectedStudents([]); // clear selections when new students load
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load students for this class');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...students];
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.student_name?.toLowerCase().includes(searchLower) ||
        s.student_reg?.toLowerCase().includes(searchLower) ||
        s.surname?.toLowerCase().includes(searchLower) ||
        s.othernames?.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort by surname
    filtered.sort((a, b) => (a.surname || '').localeCompare(b.surname || ''));
    
    setFilteredStudents(filtered);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'academic_session_id' && { term_id: '' })
    }));

    // Extract terms directly from the selected session
    if (name === 'academic_session_id' && value) {
      const selectedSession = sessions.find(s => String(s.id) === String(value));
      setTerms(selectedSession?.terms || []);
    }
  };

  const toggleStudent = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };

  const selectAll = () => {
    const currentPageStudents = getCurrentPageStudents();
    const allSelected = currentPageStudents.every(s => selectedStudents.includes(s.id));
    
    if (allSelected) {
      setSelectedStudents(prev => prev.filter(id => !currentPageStudents.some(s => s.id === id)));
    } else {
      const newSelections = currentPageStudents.map(s => s.id).filter(id => !selectedStudents.includes(id));
      setSelectedStudents(prev => [...prev, ...newSelections]);
    }
  };

  const selectAllFiltered = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  const getCurrentPageStudents = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredStudents.slice(startIndex, endIndex);
  };

  const handleAssign = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    setSubmitting(true);
    try {
      await teacherAPI.assignQuizToStudents(quizId, { 
        student_ids: selectedStudents 
      });
      toast.success(`Assigned quiz to ${selectedStudents.length} student(s)`);
      navigate(`/teacher/quizzes/${quizId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign students');
    } finally {
      setSubmitting(false);
    }
  };

  const getGenderBadge = (gender) => {
    const colors = {
      'Male': 'bg-blue-100 text-blue-800',
      'Female': 'bg-pink-100 text-pink-800',
      'Other': 'bg-purple-100 text-purple-800'
    };
    return colors[gender] || 'bg-gray-100 text-gray-800';
  };

  const currentPageStudents = getCurrentPageStudents();
  const allCurrentSelected = currentPageStudents.length > 0 && 
    currentPageStudents.every(s => selectedStudents.includes(s.id));

  if (loading && !classes.length && !sessions.length) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-xl shadow border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/>
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Assign Quiz to Students</h1>
              <p className="text-gray-600 text-sm">Select class, session, term, then choose students</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Stats Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-blue-600">Total Students</p>
              <p className="text-xl font-bold text-blue-700">{students.length}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-xs text-green-600">Filtered Students</p>
              <p className="text-xl font-bold text-green-700">{filteredStudents.length}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <p className="text-xs text-purple-600">Selected</p>
              <p className="text-xl font-bold text-purple-700">{selectedStudents.length}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600">Classes</p>
              <p className="text-xl font-bold text-gray-700">{classes.length}</p>
            </div>
          </div>

          {/* Filters Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Class *</label>
              <select
                name="class_id"
                value={filters.class_id}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Academic Session *</label>
              <select
                name="academic_session_id"
                value={filters.academic_session_id}
                onChange={handleFilterChange}
                disabled={!filters.class_id}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${!filters.class_id ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">Select Session</option>
                {sessions.map(session => (
                  <option key={session.id} value={session.id}>{session.academic_session}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Term *</label>
              <select
                name="term_id"
                value={filters.term_id}
                onChange={handleFilterChange}
                disabled={!filters.academic_session_id}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${!filters.academic_session_id ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">Select Term</option>
                {terms.map(term => (
                  <option key={term.id} value={term.id}>{term.term}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Search Bar */}
          <div className="flex gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by name or registration number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilters({
                  class_id: '',
                  academic_session_id: '',
                  term_id: '',
                });
                setSelectedStudents([]);
                setTerms([]);
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Clear All
            </button>
          </div>

          {/* Student List */}
          {!filters.class_id || !filters.academic_session_id || !filters.term_id ? (
            <div className="text-center py-12 border border-gray-200 rounded-lg">
              <i className="fas fa-filter text-4xl text-gray-300 mb-3"></i>
              <p className="text-gray-500">Please select class, session, and term to view students</p>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-12 border border-gray-200 rounded-lg">
              <i className="fas fa-users text-4xl text-gray-300 mb-3"></i>
              <p className="text-gray-500">No students found for this class, session, and term</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center flex-wrap gap-3">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={allCurrentSelected}
                      onChange={selectAll}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Select All ({currentPageStudents.length})
                    </span>
                  </div>
                  <button
                    onClick={selectAllFiltered}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {selectedStudents.length === filteredStudents.length ? 'Deselect All' : 'Select All Filtered'} ({filteredStudents.length})
                  </button>
                </div>
                <span className="text-sm text-gray-500">
                  {selectedStudents.length} of {filteredStudents.length} selected
                </span>
              </div>

              <div className="divide-y divide-gray-200 max-h-[500px] overflow-y-auto">
                {currentPageStudents.map(student => (
                  <div key={student.id} className="px-4 py-3 hover:bg-gray-50 flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => toggleStudent(student.id)}
                      className="w-4 h-4 text-blue-600 rounded flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="font-medium text-gray-900">
                          {student.student_name}
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getGenderBadge(student.sex)}`}>
                          {student.sex || 'N/A'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                        <span>Reg: {student.student_reg}</span>
                        {student.dob && <span>DOB: {new Date(student.dob).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex justify-between items-center">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate(`/teacher/quizzes/${quizId}`)}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={submitting || selectedStudents.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Assigning...
                </>
              ) : (
                `Assign to ${selectedStudents.length} Student(s)`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffAssignQuiz;